/**
 * Dummy Data for Email Channel
 *
 * This file contains mock data for UI development.
 * Replace these with actual API calls when integrating with backend.
 *
 * API Integration Notes:
 * - fetchEmailConversations() -> GET /api/email/conversations
 * - fetchEmailMessages(conversationId) -> GET /api/email/conversations/:id/messages
 * - sendEmail(data) -> POST /api/email/send
 * - saveDraft(data) -> POST /api/email/drafts
 * - fetchTemplates() -> GET /api/email/templates
 */

import {
  EmailConversation,
  EmailContact,
  EmailMessage,
} from "./types";

// ============================================
// DUMMY CONTACTS
// ============================================
export const dummyContacts: EmailContact[] = [
  {
    id: "contact-1",
    name: "John Doe",
    email: "john.doe@example.com",
    avatar: undefined,
  },
  {
    id: "contact-2",
    name: "Kim Peterson",
    email: "kim.peterson@company.com",
    avatar: undefined,
  },
  {
    id: "contact-3",
    name: "Ashish Kumar",
    email: "ashish.kumar@startup.io",
    avatar: undefined,
  },
  {
    id: "contact-4",
    name: "Sarah Wilson",
    email: "sarah.wilson@enterprise.com",
    avatar: undefined,
  },
  {
    id: "contact-5",
    name: "Rajesh Raj",
    email: "rajesh.raj@business.in",
    avatar: undefined,
  },
  {
    id: "contact-6",
    name: "Samantha Jacob",
    email: "samantha.jacob@agency.com",
    avatar: undefined,
  },
];

export const currentUser: EmailContact = {
  id: "user-1",
  name: "Support Team",
  email: "support@convoai.com",
  avatar: undefined,
};

// ============================================
// DUMMY MESSAGES
// ============================================
const createMessage = (
  id: string,
  conversationId: string,
  subject: string,
  from: EmailContact,
  to: EmailContact[],
  body: string,
  direction: "inbound" | "outbound",
  sentAt: string,
  status: EmailMessage["status"] = "read",
  isRead: boolean = true
): EmailMessage => ({
  id,
  conversationId,
  subject,
  from,
  to,
  body,
  plainText: body.replace(/<[^>]*>/g, ""),
  direction,
  status,
  sentAt,
  attachments: [],
  isRead,
});

// ============================================
// DUMMY CONVERSATIONS
// ============================================
export const dummyEmailConversations: EmailConversation[] = [
  {
    id: "conv-1",
    subject: "Product Marketplace Enquiry",
    participants: [dummyContacts[0], currentUser],
    unreadCount: 2,
    status: "open",
    createdAt: "2024-01-15T10:30:00Z",
    updatedAt: "2024-01-15T14:45:00Z",
    isStarred: true,
    lastMessage: createMessage(
      "msg-1-3",
      "conv-1",
      "Re: Product Marketplace Enquiry",
      dummyContacts[0],
      [currentUser],
      `<p>Hi Team,</p>
      <p>I came across your company browsing through LinkedIn and would like to know more about your services.</p>
      <p>Could you please share the pricing details and available packages?</p>
      <p>Looking forward to your response.</p>
      <p>Best regards,<br/>John Doe</p>`,
      "inbound",
      "2024-01-15T14:45:00Z"
    ),
    messages: [
      createMessage(
        "msg-1-1",
        "conv-1",
        "Product Marketplace Enquiry",
        dummyContacts[0],
        [currentUser],
        `<p>Hello,</p>
        <p>I am interested in your product marketplace services. Can you provide more information?</p>
        <p>Thanks,<br/>John</p>`,
        "inbound",
        "2024-01-15T10:30:00Z"
      ),
      createMessage(
        "msg-1-2",
        "conv-1",
        "Re: Product Marketplace Enquiry",
        currentUser,
        [dummyContacts[0]],
        `<p>Hi John,</p>
        <p>Thank you for reaching out! We'd be happy to help you with information about our marketplace services.</p>
        <p>Our platform offers:</p>
        <ul>
          <li>Multi-vendor support</li>
          <li>Secure payment processing</li>
          <li>Real-time inventory management</li>
        </ul>
        <p>Would you like to schedule a demo call?</p>
        <p>Best,<br/>Support Team</p>`,
        "outbound",
        "2024-01-15T11:15:00Z",
        "delivered"
      ),
      createMessage(
        "msg-1-3",
        "conv-1",
        "Re: Product Marketplace Enquiry",
        dummyContacts[0],
        [currentUser],
        `<p>Hi Team,</p>
        <p>I came across your company browsing through LinkedIn and would like to know more about your services.</p>
        <p>Could you please share the pricing details and available packages?</p>
        <p>Looking forward to your response.</p>
        <p>Best regards,<br/>John Doe</p>`,
        "inbound",
        "2024-01-15T14:45:00Z",
        "sent",
        false
      ),
    ],
  },
  {
    id: "conv-2",
    subject: "Partnership Opportunity Discussion",
    participants: [dummyContacts[1], currentUser],
    unreadCount: 0,
    status: "open",
    createdAt: "2024-01-14T09:00:00Z",
    updatedAt: "2024-01-14T16:30:00Z",
    lastMessage: createMessage(
      "msg-2-2",
      "conv-2",
      "Re: Partnership Opportunity Discussion",
      currentUser,
      [dummyContacts[1]],
      `<p>Hi Kim,</p>
      <p>We appreciate your interest in partnering with us. Our team has reviewed your proposal and we'd like to explore this further.</p>
      <p>Are you available for a call next week?</p>
      <p>Best,<br/>Support Team</p>`,
      "outbound",
      "2024-01-14T16:30:00Z",
      "read"
    ),
    messages: [
      createMessage(
        "msg-2-1",
        "conv-2",
        "Partnership Opportunity Discussion",
        dummyContacts[1],
        [currentUser],
        `<p>Hello,</p>
        <p>I represent Company XYZ and we're interested in exploring partnership opportunities with your organization.</p>
        <p>Please let me know the best way to proceed.</p>
        <p>Regards,<br/>Kim Peterson</p>`,
        "inbound",
        "2024-01-14T09:00:00Z"
      ),
      createMessage(
        "msg-2-2",
        "conv-2",
        "Re: Partnership Opportunity Discussion",
        currentUser,
        [dummyContacts[1]],
        `<p>Hi Kim,</p>
        <p>We appreciate your interest in partnering with us. Our team has reviewed your proposal and we'd like to explore this further.</p>
        <p>Are you available for a call next week?</p>
        <p>Best,<br/>Support Team</p>`,
        "outbound",
        "2024-01-14T16:30:00Z",
        "read"
      ),
    ],
  },
  {
    id: "conv-3",
    subject: "Technical Support Request",
    participants: [dummyContacts[2], currentUser],
    unreadCount: 1,
    status: "pending",
    createdAt: "2024-01-13T14:20:00Z",
    updatedAt: "2024-01-13T15:45:00Z",
    lastMessage: createMessage(
      "msg-3-1",
      "conv-3",
      "Technical Support Request",
      dummyContacts[2],
      [currentUser],
      `<p>Hi Support,</p>
      <p>I'm facing an issue with the API integration. The webhook endpoints are not receiving events properly.</p>
      <p>Error: Connection timeout after 30s</p>
      <p>Please help!</p>
      <p>Thanks,<br/>Ashish Kumar</p>`,
      "inbound",
      "2024-01-13T15:45:00Z"
    ),
    messages: [
      createMessage(
        "msg-3-1",
        "conv-3",
        "Technical Support Request",
        dummyContacts[2],
        [currentUser],
        `<p>Hi Support,</p>
        <p>I'm facing an issue with the API integration. The webhook endpoints are not receiving events properly.</p>
        <p>Error: Connection timeout after 30s</p>
        <p>Please help!</p>
        <p>Thanks,<br/>Ashish Kumar</p>`,
        "inbound",
        "2024-01-13T15:45:00Z",
        "sent",
        false
      ),
    ],
  },
  {
    id: "conv-4",
    subject: "Invoice #INV-2024-001 - Payment Confirmation",
    participants: [dummyContacts[3], currentUser],
    unreadCount: 0,
    status: "closed",
    createdAt: "2024-01-10T08:00:00Z",
    updatedAt: "2024-01-12T10:00:00Z",
    lastMessage: createMessage(
      "msg-4-3",
      "conv-4",
      "Re: Invoice #INV-2024-001 - Payment Confirmation",
      dummyContacts[3],
      [currentUser],
      `<p>Thank you for the confirmation. The payment has been processed successfully.</p>
      <p>Best,<br/>Sarah Wilson</p>`,
      "inbound",
      "2024-01-12T10:00:00Z"
    ),
    messages: [
      createMessage(
        "msg-4-1",
        "conv-4",
        "Invoice #INV-2024-001 - Payment Confirmation",
        currentUser,
        [dummyContacts[3]],
        `<p>Dear Sarah,</p>
        <p>Please find attached the invoice for your recent purchase.</p>
        <p>Total Amount: $2,500.00</p>
        <p>Due Date: January 20, 2024</p>
        <p>Thank you for your business!</p>`,
        "outbound",
        "2024-01-10T08:00:00Z",
        "read"
      ),
      createMessage(
        "msg-4-2",
        "conv-4",
        "Re: Invoice #INV-2024-001 - Payment Confirmation",
        currentUser,
        [dummyContacts[3]],
        `<p>Hi Sarah,</p>
        <p>We have received your payment. Thank you!</p>
        <p>Your receipt has been attached to this email.</p>`,
        "outbound",
        "2024-01-11T14:00:00Z",
        "read"
      ),
      createMessage(
        "msg-4-3",
        "conv-4",
        "Re: Invoice #INV-2024-001 - Payment Confirmation",
        dummyContacts[3],
        [currentUser],
        `<p>Thank you for the confirmation. The payment has been processed successfully.</p>
        <p>Best,<br/>Sarah Wilson</p>`,
        "inbound",
        "2024-01-12T10:00:00Z"
      ),
    ],
  },
  {
    id: "conv-5",
    subject: "Feedback on Recent Service",
    participants: [dummyContacts[4], currentUser],
    unreadCount: 0,
    status: "closed",
    createdAt: "2024-01-08T11:00:00Z",
    updatedAt: "2024-01-08T12:30:00Z",
    lastMessage: createMessage(
      "msg-5-1",
      "conv-5",
      "Feedback on Recent Service",
      dummyContacts[4],
      [currentUser],
      `<p>Hello,</p>
      <p>I wanted to share my feedback on the recent service I received. The team was very professional and resolved my issue quickly.</p>
      <p>Great job!</p>
      <p>Regards,<br/>Rajesh Raj</p>`,
      "inbound",
      "2024-01-08T12:30:00Z"
    ),
    messages: [
      createMessage(
        "msg-5-1",
        "conv-5",
        "Feedback on Recent Service",
        dummyContacts[4],
        [currentUser],
        `<p>Hello,</p>
        <p>I wanted to share my feedback on the recent service I received. The team was very professional and resolved my issue quickly.</p>
        <p>Great job!</p>
        <p>Regards,<br/>Rajesh Raj</p>`,
        "inbound",
        "2024-01-08T12:30:00Z"
      ),
    ],
  },
  {
    id: "conv-6",
    subject: "Meeting Request - Q1 Planning",
    participants: [dummyContacts[5], currentUser],
    unreadCount: 1,
    status: "open",
    createdAt: "2024-01-15T09:00:00Z",
    updatedAt: "2024-01-15T09:00:00Z",
    lastMessage: createMessage(
      "msg-6-1",
      "conv-6",
      "Meeting Request - Q1 Planning",
      dummyContacts[5],
      [currentUser],
      `<p>Hi Team,</p>
      <p>I would like to schedule a meeting to discuss our Q1 planning and strategy alignment.</p>
      <p>Please let me know your availability for next week.</p>
      <p>Thanks,<br/>Samantha Jacob</p>`,
      "inbound",
      "2024-01-15T09:00:00Z"
    ),
    messages: [
      createMessage(
        "msg-6-1",
        "conv-6",
        "Meeting Request - Q1 Planning",
        dummyContacts[5],
        [currentUser],
        `<p>Hi Team,</p>
        <p>I would like to schedule a meeting to discuss our Q1 planning and strategy alignment.</p>
        <p>Please let me know your availability for next week.</p>
        <p>Thanks,<br/>Samantha Jacob</p>`,
        "inbound",
        "2024-01-15T09:00:00Z",
        "sent",
        false
      ),
    ],
  },
];


// ============================================
// HELPER FUNCTIONS (For API Integration)
// ============================================

/**
 * Simulates fetching email conversations from API
 * Replace with actual API call: GET /api/email/conversations
 */
export const fetchEmailConversations = async (): Promise<
  EmailConversation[]
> => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 500));
  return dummyEmailConversations;
};

/**
 * Simulates fetching messages for a conversation
 * Replace with actual API call: GET /api/email/conversations/:id/messages
 */
export const fetchEmailMessages = async (
  conversationId: string
): Promise<EmailMessage[]> => {
  await new Promise((resolve) => setTimeout(resolve, 300));
  const conversation = dummyEmailConversations.find(
    (c) => c.id === conversationId
  );
  return conversation?.messages || [];
};

/**
 * Simulates sending an email
 * Replace with actual API call: POST /api/email/send
 */
export const sendEmail = async (data: {
  to: string[];
  subject: string;
  body: string;
}): Promise<{ success: boolean; messageId: string }> => {
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return { success: true, messageId: `msg-${Date.now()}` };
};

/**
 * Simulates saving a draft
 * Replace with actual API call: POST /api/email/drafts
 */
export const saveDraft = async (data: {
  to: string[];
  subject: string;
  body: string;
}): Promise<{ success: boolean; draftId: string }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true, draftId: `draft-${Date.now()}` };
};

